import { RefObject, useCallback, useEffect, useRef } from "react";

export function useStickyBarLayout(
  barRef: RefObject<HTMLDivElement | null>,
  testId?: string,
) {
  const rafId = useRef<number | null>(null);

  const setLayoutHeights = useCallback(() => {
    // Build the selector safely.
    const selector = testId
      ? `[data-testid="${CSS.escape(testId)}"] > div:first-child`
      : '[data-testid="header-outerContainer"] > div:first-child';

    const header = document.querySelector(selector);
    const headerHeight = header?.clientHeight ?? 0;

    document.documentElement.style.setProperty(
      "--headerHeight",
      `${headerHeight}px`,
    );

    if (barRef.current) {
      const stickyHeight = barRef.current.clientHeight;
      document.documentElement.style.setProperty(
        "--newStickyHeight",
        `${headerHeight + stickyHeight}px`,
      );
    }
  }, [barRef, testId]);

  const scheduleLayoutHeights = useCallback(() => {
    if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      setLayoutHeights();
    });
  }, [setLayoutHeights]);

  useEffect(() => {
    setLayoutHeights();
    window.addEventListener("resize", scheduleLayoutHeights);
    window.addEventListener("scroll", scheduleLayoutHeights, { passive: true });

    return () => {
      window.removeEventListener("resize", scheduleLayoutHeights);
      window.removeEventListener("scroll", scheduleLayoutHeights);

      if (rafId.current !== null) cancelAnimationFrame(rafId.current);
    };
  }, [setLayoutHeights, scheduleLayoutHeights]);

  return { setLayoutHeights };
}
